/**
 * Nguồn sự thật về chế độ giao diện cho toàn web.
 *
 * Trước khi React mount, script inline trong public/index.html đã đặt class
 * "dark" để không nháy trắng. Provider này nhận lại vai trò đó từ lúc mount,
 * và là nơi duy nhất cập nhật class/meta về sau.
 */

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { applyTheme, getMode, resolveDark, setMode as persistMode } from './theme-storage';

const ThemeContext = createContext({
    mode: 'system',
    isDark: false,
    setMode: () => {},
});

export function useThemeMode() {
    return useContext(ThemeContext);
}

export default function ThemeProvider({ children }) {
    const [mode, setModeState] = useState(getMode);
    const [isDark, setIsDark] = useState(() => resolveDark(getMode()));

    // Áp chế độ mỗi khi lựa chọn đổi
    useEffect(() => {
        setIsDark(applyTheme(mode));
    }, [mode]);

    // Theo dõi cài đặt hệ điều hành — CHỈ khi người dùng để mức "Theo hệ thống".
    // Chọn cứng Sáng/Tối thì không đăng ký listener, tránh bị máy ghi đè.
    useEffect(() => {
        if (mode !== 'system') return undefined;
        if (typeof window.matchMedia !== 'function') return undefined;

        const query = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => setIsDark(applyTheme('system'));

        // Safari cũ chỉ có addListener
        if (query.addEventListener) {
            query.addEventListener('change', handleChange);
            return () => query.removeEventListener('change', handleChange);
        }
        query.addListener(handleChange);
        return () => query.removeListener(handleChange);
    }, [mode]);

    const setMode = useCallback((next) => {
        persistMode(next);
        setModeState(next);
    }, []);

    const value = useMemo(() => ({ mode, isDark, setMode }), [mode, isDark, setMode]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
